import type { TableSynchronisee } from '../../../db/schema.ts'

export type Table = TableSynchronisee

/** Une ligne, vue des deux côtés. Volontairement lâche : la couche
 *  d'opérations manipule des champs, pas des entités typées — c'est ce qui
 *  lui permet d'être exactement la même en SQL et en mémoire. */
export type Ligne = Record<string, unknown> & { id: string; deletedAt?: unknown }

/**
 * Ce qu'une opération produit. Jamais du SQL, jamais un objet React : une
 * description de changement que les deux adaptateurs savent appliquer.
 */
export type Mutation =
  | { type: 'creer'; table: Table; ligne: Ligne }
  | { type: 'modifier'; table: Table; id: string; champs: Record<string, unknown> }
  | { type: 'supprimer'; table: Table; id: string }

export type Statut = 'applied' | 'rejected' | 'adjusted'

export type Resultat = {
  statut: Statut
  mutations: Mutation[]
  /** Code machine, pour l'UI et les tests. */
  raison?: string
  /** Phrase française montrée à la personne. Voir le bandeau d'ajustements. */
  message?: string
}

/** Accès en lecture à l'état, fourni par l'adaptateur (SQL ou mémoire). */
export type Lecture = {
  get(table: Table, id: string): Ligne | undefined
  tous(table: Table): Ligne[]
}

/** Contexte d'application : qui, quand, et où on en est. */
export type Contexte = {
  householdId: string
  userId: string
  /** Horloge SERVEUR. Sur le client, c'est Date.now() corrigé de l'offset. */
  maintenant: Date
  /** `rev` courant du foyer. Sert aux règles de causalité. */
  revCourant: number
}

/** L'enveloppe d'une intention, telle qu'elle voyage. */
export type Op = {
  opId: string
  deviceSeq: number
  /**
   * Le `rev` que l'appareil avait déjà vu au moment du geste. SEULE
   * information de causalité du protocole : elle distingue « il savait et a
   * quand même choisi ça » de « il ne pouvait pas savoir ».
   */
  baseRev: number
  clientAt: number
  kind: string
  payload: Record<string, unknown>
}

export const applique = (mutations: Mutation[]): Resultat => ({ statut: 'applied', mutations })

export const rejete = (raison: string, message: string): Resultat => ({
  statut: 'rejected',
  mutations: [],
  raison,
  message,
})

/** Écartée, mais sans que ce soit une erreur : le geste était légitime, il a
 *  simplement été dépassé par un fait plus fort. L'UI le dit, nommément. */
export const ajuste = (raison: string, message: string): Resultat => ({
  statut: 'adjusted',
  mutations: [],
  raison,
  message,
})
